import React from "react";
import { api, API } from "@/lib/config";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Loader2, Send, FileDown } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  declined: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function Estimates() {
  const [items, setItems] = React.useState([]);
  const [creating, setCreating] = React.useState(false);
  const [form, setForm] = React.useState({
    customer_name: "", customer_email: "", customer_address: "",
    project_title: "", tax_rate: 0, notes: "",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
  });

  const load = () => api.get("/admin/estimates").then((r) => setItems(r.data.items));
  React.useEffect(() => { load(); }, []);

  const updateLine = (idx, patch) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const subtotal = form.line_items.reduce((s, li) => s + (Number(li.quantity)||0) * (Number(li.unit_price)||0), 0);
  const tax = subtotal * (Number(form.tax_rate)||0) / 100;
  const total = subtotal + tax;

  const save = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...form,
        tax_rate: Number(form.tax_rate) || 0,
        line_items: form.line_items.map((li) => ({
          description: li.description, quantity: Number(li.quantity)||0, unit_price: Number(li.unit_price)||0,
        })),
      };
      await api.post("/admin/estimates", payload);
      toast.success("Estimate created.");
      setForm({ customer_name: "", customer_email: "", customer_address: "", project_title: "", tax_rate: 0, notes: "", line_items: [{ description: "", quantity: 1, unit_price: 0 }] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save.");
    } finally { setCreating(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/estimates/${id}`, { status }); load(); }
    catch { toast.error("Update failed"); }
  };

  const sendEmail = async (id) => {
    const t = toast.loading("Sending estimate...");
    try {
      await api.post(`/admin/estimates/${id}/send`);
      toast.success("Estimate sent.", { id: t });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed.", { id: t }); }
  };

  return (
    <div data-testid="admin-estimates" className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={save} data-testid="estimate-form" className="lg:col-span-5 rounded-2xl border border-black/10 bg-white p-6 h-fit space-y-4">
        <div className="eyebrow text-[color:var(--brand-wood)]">New estimate</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Customer name</Label>
            <Input required data-testid="est-cust-name" value={form.customer_name} onChange={(e)=>setForm({...form, customer_name:e.target.value})}/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Email</Label>
            <Input type="email" required data-testid="est-cust-email" value={form.customer_email} onChange={(e)=>setForm({...form, customer_email:e.target.value})}/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Tax rate (%)</Label>
            <Input type="number" min="0" step="0.01" data-testid="est-tax" value={form.tax_rate} onChange={(e)=>setForm({...form, tax_rate:e.target.value})}/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Address</Label>
            <Input data-testid="est-cust-address" value={form.customer_address} onChange={(e)=>setForm({...form, customer_address:e.target.value})}/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Project title</Label>
            <Input required data-testid="est-project" value={form.project_title} onChange={(e)=>setForm({...form, project_title:e.target.value})}/>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest">Line items</Label>
            <button type="button" onClick={addLine} data-testid="est-add-line" className="text-xs inline-flex items-center gap-1 text-[color:var(--brand-woodDark)] hover:text-[color:var(--brand-ink)]">
              <Plus className="h-3.5 w-3.5"/> Add line
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {form.line_items.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`est-line-${i}`}>
                <Input className="col-span-6" placeholder="Description" value={li.description} onChange={(e)=>updateLine(i,{description:e.target.value})} required/>
                <Input className="col-span-2" type="number" step="0.01" placeholder="Qty" value={li.quantity} onChange={(e)=>updateLine(i,{quantity:e.target.value})}/>
                <Input className="col-span-3" type="number" step="0.01" placeholder="Unit $" value={li.unit_price} onChange={(e)=>updateLine(i,{unit_price:e.target.value})}/>
                <button type="button" onClick={()=>removeLine(i)} disabled={form.line_items.length<=1} className="col-span-1 justify-self-center text-red-600 disabled:opacity-30">
                  <Trash2 className="h-4 w-4"/>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest">Notes</Label>
          <Textarea rows={3} data-testid="est-notes" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})}/>
        </div>

        <div className="rounded-xl bg-[color:var(--brand-cream)] border border-black/5 p-4 text-sm space-y-1">
          <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-semibold text-[color:var(--brand-ink)]"><span>Total</span><span data-testid="est-preview-total">${total.toFixed(2)}</span></div>
        </div>

        <button type="submit" disabled={creating} data-testid="est-save"
          className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-5 py-2.5 text-sm disabled:opacity-60">
          {creating ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
          Save estimate
        </button>
      </form>

      <div className="lg:col-span-7">
        {items.length === 0 ? (
          <div data-testid="estimates-empty" className="rounded-2xl border border-dashed border-black/10 p-10 text-center text-sm text-[color:var(--brand-woodDark)]">
            No estimates yet.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`est-row-${it.number}`} className="rounded-2xl border border-black/10 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="font-display text-xl">{it.number}</div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_STYLE[it.status] || ""}`}>{it.status}</span>
                    </div>
                    <div className="text-sm mt-1">{it.customer_name} · <span className="text-[color:var(--brand-woodDark)]">{it.customer_email}</span></div>
                    <div className="text-xs text-[color:var(--brand-woodDark)] mt-1">{it.project_title}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl">${it.total.toFixed(2)}</div>
                    <div className="text-xs text-[color:var(--brand-woodDark)]">{new Date(it.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <a href={`${API}/admin/estimates/${it.id}/pdf`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs rounded-full border border-black/10 px-3 py-1.5 hover:bg-black/5"
                    data-testid={`est-pdf-${it.number}`}>
                    <FileDown className="h-3.5 w-3.5"/> Download PDF
                  </a>
                  <button onClick={()=>sendEmail(it.id)} data-testid={`est-send-${it.number}`}
                    className="inline-flex items-center gap-1 text-xs rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-3 py-1.5">
                    <Send className="h-3.5 w-3.5"/> Send to customer
                  </button>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-[color:var(--brand-woodDark)]">Status:</span>
                    <Select value={it.status} onValueChange={(v)=>updateStatus(it.id, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid={`est-status-${it.number}`}><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
